import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Citrus, Lock, Loader2, Truck, LogOut, PackageCheck, RefreshCw, Star, Check, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARRIERS = ['Correos', 'Correos Express', 'SEUR', 'MRW', 'GLS', 'DHL', 'UPS', 'DPD', 'Other'];
const ROUTE_LABELS = { ES: 'Spain', DE: 'Germany', FR: 'France', NL: 'Netherlands', ROW: 'Rest of Europe / Worldwide' };

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem('at_admin_token') || '');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [orders, setOrders] = useState(null);
  const [shipForm, setShipForm] = useState({});
  const [shippingId, setShippingId] = useState('');
  const [stock, setStock] = useState('');
  const [rates, setRates] = useState([]);
  const [configSaving, setConfigSaving] = useState(false);
  const [adminReviews, setAdminReviews] = useState([]);

  const loadReviews = useCallback(async (tk) => {
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setAdminReviews(data);
    } catch (e) {
      // reviews best-effort
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/store/config`);
      setStock(String(data.stock));
      setRates(data.shipping_rates);
    } catch (e) {
      // config is best-effort in admin
    }
  }, []);

  const loadOrders = useCallback(async (tk) => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setOrders(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('at_admin_token');
        setToken('');
        setOrders(null);
      } else {
        toast.error('Could not load orders');
      }
    }
  }, []);

  useEffect(() => {
    if (token) {
      loadOrders(token);
      loadConfig();
      loadReviews(token);
    }
  }, [token, loadOrders, loadConfig, loadReviews]);

  const approveReview = async (id) => {
    try {
      await axios.post(`${API}/admin/reviews/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review approved and live on the site');
      loadReviews(token);
    } catch (e) {
      toast.error('Could not approve review');
    }
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review deleted');
      loadReviews(token);
    } catch (e) {
      toast.error('Could not delete review');
    }
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const stockVal = parseInt(stock, 10);
      if (Number.isNaN(stockVal) || stockVal < 0) {
        toast.error('Stock must be a number of 0 or more');
        return;
      }
      await axios.put(`${API}/admin/stock`, { stock: stockVal }, { headers });
      await axios.put(`${API}/admin/shipping-rates`, {
        rates: rates.map((r) => ({
          route: r.route,
          fee_cents: r.fee_eur !== undefined && r.fee_eur !== ''
            ? Math.round(parseFloat(r.fee_eur) * 100)
            : r.fee_cents,
        })),
      }, { headers });
      toast.success('Store settings saved');
      loadConfig();
    } catch (e) {
      toast.error('Could not save settings');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('at_admin_token', data.token);
      setToken(data.token);
    } catch (err) {
      toast.error('Wrong password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleShip = async (sessionId) => {
    const form = shipForm[sessionId] || {};
    if (!form.carrier || !form.tracking_number) {
      toast.error('Carrier and tracking number are required');
      return;
    }
    setShippingId(sessionId);
    try {
      await axios.post(`${API}/admin/orders/${sessionId}/ship`, {
        carrier: form.carrier,
        tracking_number: form.tracking_number,
        tracking_url: form.tracking_url || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Marked as shipped — tracking email sent to buyer');
      loadOrders(token);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not mark as shipped');
    } finally {
      setShippingId('');
    }
  };

  const setField = (sessionId, field, value) =>
    setShipForm((prev) => ({ ...prev, [sessionId]: { ...(prev[sessionId] || {}), [field]: value } }));

  if (!token) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
        <div className="max-w-sm w-full bg-white border-2 border-brand-green/15 rounded-3xl p-8 shadow-xl" data-testid="admin-login-card">
          <div className="w-12 h-12 rounded-full bg-brand-orange mx-auto flex items-center justify-center text-white -rotate-6 mb-4">
            <Citrus className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl uppercase text-brand-green text-center">Admin Access</h1>
          <p className="text-xs text-brand-green/60 text-center mt-1 mb-6">Angry Tangerine order management</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="admin-password" className="block text-xs font-bold uppercase tracking-wider text-brand-green mb-2">Admin Password</label>
              <input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-brand-green/20 bg-brand-cream text-sm text-brand-green focus:outline-none focus:ring-2 focus:ring-brand-orange"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loginLoading}
              className="w-full py-3 rounded-2xl bg-brand-orange text-white font-extrabold uppercase tracking-wider hover:bg-brand-citrus transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
            >
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Log In
            </button>
          </form>
        </div>
      </div>
    );
  }

  const paidOrders = orders || [];
  const totalRevenue = paidOrders.reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0);
  const bottlesSold = paidOrders.reduce((sum, o) => sum + (parseInt(o.quantity, 10) || 1), 0);
  const awaitingFulfilment = paidOrders.filter((o) => o.status !== 'shipped').length;
  const pendingReviews = adminReviews.filter((r) => !r.approved).length;

  return (
    <div className="min-h-screen bg-brand-cream text-brand-green">
      <header className="border-b border-brand-green/10 bg-brand-cream/90 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-brand-orange flex items-center justify-center text-white -rotate-6">
              <Citrus className="w-4 h-4" />
            </div>
            <span className="font-display text-xl uppercase text-brand-green">Orders Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => loadOrders(token)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-brand-green/20 text-xs font-bold text-brand-green hover:border-brand-orange"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={() => { localStorage.removeItem('at_admin_token'); setToken(''); setOrders(null); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-green text-white text-xs font-bold hover:bg-brand-leaf"
            >
              <LogOut className="w-3.5 h-3.5" /> Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <div className="bg-brand-green text-brand-cream rounded-2xl p-5" data-testid="stat-revenue">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-brand-cream/60 mb-1">Revenue</span>
            <span className="font-display text-3xl">€{totalRevenue.toFixed(2)}</span>
            <span className="block text-[11px] text-brand-cream/60 mt-1">{paidOrders.length} paid order{paidOrders.length === 1 ? '' : 's'}</span>
          </div>
          <div className="bg-white border border-brand-green/10 rounded-2xl p-5" data-testid="stat-bottles">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-brand-green/50 mb-1">Bottles Sold</span>
            <span className="font-display text-3xl text-brand-orange">{bottlesSold}</span>
            <span className="block text-[11px] text-brand-green/50 mt-1">{stock === '' ? '—' : stock} left in stock</span>
          </div>
          <div className="bg-white border border-brand-green/10 rounded-2xl p-5" data-testid="stat-fulfilment">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-brand-green/50 mb-1">Awaiting Fulfilment</span>
            <span className="font-display text-3xl">{awaitingFulfilment}</span>
            <span className="block text-[11px] text-brand-green/50 mt-1">{paidOrders.length - awaitingFulfilment} shipped</span>
          </div>
          <div className="bg-white border border-brand-green/10 rounded-2xl p-5" data-testid="stat-reviews">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-brand-green/50 mb-1">Reviews</span>
            <span className="font-display text-3xl text-brand-orange">{pendingReviews} pending</span>
            <span className="block text-[11px] text-brand-green/50 mt-1">{adminReviews.length - pendingReviews} live on site</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div className="bg-white border border-brand-green/10 rounded-2xl p-5" data-testid="stock-editor">
            <h3 className="font-display text-xl uppercase mb-1">Stock Counter</h3>
            <p className="text-xs text-brand-green/60 mb-3">Shown to buyers as "only X bottles left". Decreases automatically with each paid order.</p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                data-testid="stock-input"
                value={stock}
                onChange={(e) => setStock(e.target.value)}
                className="w-32 px-3 py-2 rounded-lg border border-brand-green/20 bg-brand-cream text-sm font-bold focus:outline-none focus:ring-2 focus:ring-brand-orange"
              />
              <span className="text-xs text-brand-green/60">bottles in stock</span>
            </div>
          </div>

          <div className="bg-white border border-brand-green/10 rounded-2xl p-5" data-testid="shipping-editor">
            <h3 className="font-display text-xl uppercase mb-1">Shipping Fees</h3>
            <p className="text-xs text-brand-green/60 mb-3">Charged at checkout by delivery country. 0 = free shipping.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {rates.map((r) => (
                <div key={r.route}>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-brand-green/70 mb-1">{ROUTE_LABELS[r.route] || r.route}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.05"
                    data-testid={`shipping-fee-${r.route}`}
                    value={r.fee_eur !== undefined ? r.fee_eur : (r.fee_cents / 100).toString()}
                    onChange={(e) => setRates((prev) => prev.map((x) => x.route === r.route ? { ...x, fee_eur: e.target.value } : x))}
                    className="w-full px-3 py-2 rounded-lg border border-brand-green/20 bg-brand-cream text-sm font-bold focus:outline-none focus:ring-2 focus:ring-brand-orange"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-10 flex justify-end">
          <button
            data-testid="save-config-btn"
            onClick={saveConfig}
            disabled={configSaving}
            className="px-6 py-2.5 rounded-xl bg-brand-green text-white text-xs font-extrabold uppercase tracking-wider hover:bg-brand-leaf transition-colors disabled:opacity-70 inline-flex items-center gap-2"
          >
            {configSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save Store Settings
          </button>
        </div>

        <h2 className="font-display text-3xl uppercase mb-6">Paid Orders</h2>
        {orders === null && (
          <p className="text-sm text-brand-green/60 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading orders…</p>
        )}
        {orders !== null && orders.length === 0 && (
          <p className="text-sm text-brand-green/60" data-testid="admin-no-orders">No paid orders yet.</p>
        )}
        <div className="space-y-4" data-testid="admin-orders-table">
          {(orders || []).map((o) => (
            <div key={o.session_id} className="bg-white border border-brand-green/10 rounded-2xl p-5" data-testid={`order-row-${o.session_id}`}>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                <span className="font-mono text-xs text-brand-green/60">{o.session_id.slice(0, 18)}…</span>
                <span className="font-bold">{o.quantity} × bottle{o.quantity > 1 ? 's' : ''}</span>
                <span className="font-bold text-brand-orange">€{o.amount}</span>
                <span className="text-xs">{o.email || '—'}</span>
                <span className="text-xs">{o.country || '—'}</span>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-brand-green/10">{o.provider}</span>
                {o.status === 'shipped' ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-leaf" data-testid={`order-shipped-badge-${o.session_id}`}>
                    <PackageCheck className="w-4 h-4" /> Shipped · {o.carrier} · {o.tracking_number}
                  </span>
                ) : (
                  <span className="text-[11px] font-bold text-brand-orange uppercase">Awaiting fulfilment</span>
                )}
              </div>

              {o.status !== 'shipped' && (
                <div className="mt-4 pt-4 border-t border-brand-green/10 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-brand-green/70 mb-1">Carrier</label>
                    <select
                      data-testid={`ship-carrier-${o.session_id}`}
                      value={(shipForm[o.session_id] || {}).carrier || ''}
                      onChange={(e) => setField(o.session_id, 'carrier', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-brand-green/20 bg-brand-cream text-xs font-bold focus:outline-none focus:ring-2 focus:ring-brand-orange"
                    >
                      <option value="">Select…</option>
                      {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-brand-green/70 mb-1">Tracking Number</label>
                    <input
                      data-testid={`ship-tracking-${o.session_id}`}
                      value={(shipForm[o.session_id] || {}).tracking_number || ''}
                      onChange={(e) => setField(o.session_id, 'tracking_number', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-brand-green/20 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      placeholder="e.g. PQ4F6A0701234567890"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-brand-green/70 mb-1">Tracking URL (optional)</label>
                    <input
                      data-testid={`ship-url-${o.session_id}`}
                      value={(shipForm[o.session_id] || {}).tracking_url || ''}
                      onChange={(e) => setField(o.session_id, 'tracking_url', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-brand-green/20 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      placeholder="https://…"
                    />
                  </div>
                  <button
                    data-testid={`ship-submit-${o.session_id}`}
                    onClick={() => handleShip(o.session_id)}
                    disabled={shippingId === o.session_id}
                    className="py-2.5 rounded-xl bg-brand-orange text-white text-xs font-extrabold uppercase tracking-wider hover:bg-brand-citrus transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
                  >
                    {shippingId === o.session_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                    Ship + Email Buyer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <h2 className="font-display text-3xl uppercase mt-14 mb-6">Customer Reviews</h2>
        {adminReviews.length === 0 && (
          <p className="text-sm text-brand-green/60" data-testid="admin-no-reviews">No reviews submitted yet.</p>
        )}
        <div className="space-y-4" data-testid="admin-reviews-list">
          {adminReviews.map((r) => (
            <div key={r.id} className="bg-white border border-brand-green/10 rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid={`admin-review-${r.id}`}>
              {r.photo_url && (
                <img src={r.photo_url} alt="review pet" className="w-16 h-16 rounded-xl object-cover border border-brand-green/10" />
              )}
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">{r.name}</span>
                  <span className="inline-flex gap-0.5 text-brand-orange">
                    {[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3 h-3" fill="currentColor" />)}
                  </span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.approved ? 'bg-brand-leaf/10 text-brand-leaf' : 'bg-brand-orange/10 text-brand-orange'}`} data-testid={`review-status-${r.id}`}>
                    {r.approved ? 'Live' : 'Pending'}
                  </span>
                </div>
                <p className="text-xs text-brand-green/70 mt-1 leading-relaxed">{r.text}</p>
              </div>
              <div className="flex items-center gap-2">
                {!r.approved && (
                  <button
                    data-testid={`approve-review-${r.id}`}
                    onClick={() => approveReview(r.id)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-green text-white text-xs font-bold hover:bg-brand-leaf transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                )}
                <button
                  data-testid={`delete-review-${r.id}`}
                  onClick={() => deleteReview(r.id)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-red-300 text-red-600 text-xs font-bold hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
